import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { type FilterConfig } from '@analysis/domain';

interface FilterConfigState {
  configs: EntityState<FilterConfig>;
  selected: string | undefined;
  loading: boolean;
  loaded: boolean;
  error: string | undefined;
}

const initialState: FilterConfigState = {
  configs: {
    ids: [],
    entities: {},
  },
  selected: undefined,
  loading: false,
  loaded: false,
  error: undefined,
};

@Injectable()
export class FilterConfigStore extends ComponentStore<FilterConfigState> {
  private readonly adapter: EntityAdapter<FilterConfig>;

  constructor() {
    super(initialState);

    this.adapter = createEntityAdapter<FilterConfig>({
      selectId: (config) => config.id,
    });
  }

  // #region Selectors

  public readonly configs$ = this.select((state) => Object.values(state.configs.entities));
  public readonly selectedConfig$ = this.select((state) => state.configs.entities[state.selected]);
  public readonly loading$ = this.select((state) => state.loading);
  public readonly loaded$ = this.select((state) => state.loaded);
  public readonly error$ = this.select((state) => state.error);

  // #endregion

  // #region Updaters

  public readonly setLoading = this.updater((state, loading: boolean) => ({
    ...state,
    loading,
    error: loading ? undefined : state.error,
  }));

  public readonly setConfigs = this.updater((state, configs: FilterConfig[]) => ({
    ...state,
    configs: this.adapter.setAll(configs, state.configs),
    loading: false,
    loaded: true,
    error: undefined,
  }));

  public readonly addConfig = this.updater((state, config: FilterConfig) => ({
    ...state,
    configs: this.adapter.upsertOne(config, state.configs),
    loading: false,
    error: undefined,
  }));

  public readonly removeConfig = this.updater((state, configId: string) => ({
    ...state,
    configs: this.adapter.removeOne(configId, state.configs),
    loading: false,
    error: undefined,
  }));

  public readonly setError = this.updater((state, error: string) => ({
    ...state,
    loading: false,
    error,
  }));

  public readonly selectConfig = this.updater((state, configId: string) => ({
    ...state,
    selected: configId,
  }));

  // #endregion
}
